import {
  Menu,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import { EllipsisVertical } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { TableReduction } from 'pages/Availability/common/ReductionsTable/types';
import { getReductionActionPermissions } from 'pages/Availability/common/ReductionsTable/utils';
import {
  BaseAssetAvailabilityEntry,
  BaseSiteAvailabilityEntry,
} from 'pages/Availability/types';
import { useState } from 'react';
import { useStore } from 'store';

type ReductionsTableActionsProps =
  | {
      reduction: TableReduction<BaseSiteAvailabilityEntry>;
      availabilityControl: SiteAvailabilityControl.SiteLevel;
    }
  | {
      reduction: TableReduction<BaseAssetAvailabilityEntry>;
      availabilityControl: SiteAvailabilityControl.AssetLevel;
    };

export const ReductionsTableActions: React.FC<ReductionsTableActionsProps> = ({
  reduction,
  availabilityControl,
}) => {
  const {
    setAvailabilityUserAction,
    setAvailabilityFormOpen,
    resetPersistedSiteAvailabilityFormData,
    resetPersistedAssetAvailabilityFormData,
  } = useStore((state) => ({
    setAvailabilityUserAction: state.setAvailabilityUserAction,
    setAvailabilityFormOpen: state.setAvailabilityFormOpen,
    resetPersistedSiteAvailabilityFormData:
      state.resetPersistedSiteAvailabilityFormData,
    resetPersistedAssetAvailabilityFormData:
      state.resetPersistedAssetAvailabilityFormData,
  }));

  const [permissions, setPermissions] = useState(() =>
    getReductionActionPermissions(reduction),
  );

  const getLabel = (enabled: boolean, label: string) =>
    enabled ? <b>{label}</b> : label;

  const handleMenuItemClick = (
    actionType: 'edit' | 'cancel' | 'end' | 'endAndStartNew' | 'info',
  ) => {
    if (availabilityControl === SiteAvailabilityControl.SiteLevel) {
      setAvailabilityUserAction({
        actionType,
        assetAvailabilityEntry: null,
        siteAvailabilityEntry: reduction,
      });
    } else if (availabilityControl === SiteAvailabilityControl.AssetLevel) {
      setAvailabilityUserAction({
        actionType,
        siteAvailabilityEntry: null,
        assetAvailabilityEntry: reduction,
      });
    }

    if (availabilityControl === SiteAvailabilityControl.SiteLevel) {
      resetPersistedSiteAvailabilityFormData();
    } else if (availabilityControl === SiteAvailabilityControl.AssetLevel) {
      resetPersistedAssetAvailabilityFormData();
    }

    if (actionType === 'edit') {
      setAvailabilityFormOpen(true);
    }
  };

  return (
    <Popover>
      <PopoverTrigger
        buttonProps={{
          icon: <EllipsisVertical />,
          variant: Variants.Transparent,
        }}
        data-testid="reduction-table-actions-button"
        style={{
          minWidth: 'unset',
        }}
        onClick={() => {
          // Update permissions on each open to ensure they are current
          setPermissions(getReductionActionPermissions(reduction));
        }}
      />
      <PopoverContent style={{ zIndex: 100 }}>
        <Menu
          items={[
            {
              label: getLabel(permissions.enableEdit, 'Edit time...'),
              key: '1',
              disabled: !permissions.enableEdit,
              onClick: () => handleMenuItemClick('edit'),
            },
            {
              label: getLabel(
                permissions.enableEndAndStartNew,
                'End and start new...',
              ),
              key: '2',
              disabled: !permissions.enableEndAndStartNew,
              onClick: () => handleMenuItemClick('endAndStartNew'),
            },
            {
              label: getLabel(permissions.enableEnd, 'End'),
              key: '3',
              disabled: !permissions.enableEnd,
              onClick: () => handleMenuItemClick('end'),
            },
            {
              label: getLabel(permissions.enableCancel, 'Cancel'),
              key: '4',
              disabled: !permissions.enableCancel,
              onClick: () => handleMenuItemClick('cancel'),
            },
            {
              label: getLabel(!!reduction, 'Get info'),
              key: '5',
              disabled: !permissions.enableInfo,
              onClick: () => handleMenuItemClick('info'),
            },
          ]}
        />
      </PopoverContent>
    </Popover>
  );
};
